import { TResponse, Notifier, TIsOk, TWithIsOk } from '../../types';
import { Fetcher, getErrorMessage } from '../../utils';

import { AuthRepository } from './Auth.repository';
import { IAuthLoginUser, IAuthSignUpUser, IIssuedMcpToken, IListedMcpToken } from './types';

export class AuthService {
  private repository: AuthRepository;
  private notifier: Notifier;

  constructor(fetcher: Fetcher, notifier: Notifier) {
    this.repository = new AuthRepository(fetcher);
    this.notifier = notifier;
  }

  public async signUp(user: IAuthSignUpUser): Promise<TIsOk> {
    let response: TResponse<{ id: string }>;

    try {
      response = await this.repository.signUp(user);
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false };
    }

    return { isOk: true };
  }

  public async login(user: IAuthLoginUser): Promise<TIsOk> {
    let response: TResponse<void>;

    try {
      response = await this.repository.login(user);
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false };
    }

    return { isOk: true };
  }

  public async logout(): Promise<TIsOk> {
    let response: TResponse<{ id: string }>;

    try {
      response = await this.repository.logout();
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false };
    }

    return { isOk: true };
  }

  public async issueMcpToken(name: string): Promise<TWithIsOk<{ token?: string }>> {
    let response: TResponse<IIssuedMcpToken>;

    try {
      response = await this.repository.issueMcpToken(name);
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false };
    }

    return { isOk: true, token: response.data?.token };
  }

  public async listMcpTokens(): Promise<TWithIsOk<{ tokens: IListedMcpToken[] }>> {
    let response: TResponse<{ tokens: IListedMcpToken[] }>;

    try {
      response = await this.repository.listMcpTokens();
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false, tokens: [] };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false, tokens: [] };
    }

    return { isOk: true, tokens: response.data?.tokens ?? [] };
  }

  public async revokeMcpToken(tokenId: string): Promise<TIsOk> {
    let response: TResponse<{ message: string }>;

    try {
      response = await this.repository.revokeMcpToken(tokenId);
    } catch (err) {
      this.notifier.error(getErrorMessage(err));
      return { isOk: false };
    }

    if (response.error) {
      this.notifier.error(response.error.toString());
      return { isOk: false };
    }

    return { isOk: true };
  }
}
